// src/stores/progress.js
import { defineStore } from 'pinia'
import { LocalStorage, Notify } from 'quasar'
import { fbDatabase } from 'src/boot/firebase'
import { addItemToCsvList, removeItemFromCsvList } from 'src/functions/function-lists'
import { updateDbChild, incrementDbChild } from 'src/functions/function-fb-crud'
import { sendAnalytics } from 'src/functions/function-fb-analytics'
import { logIt } from 'src/functions/function-logger'
import { useUserStore } from './store-user'
import { usePacksStore } from 'src/stores/store-packs'

// uid whose progress listeners are attached: onUserFetched runs again on every change of users/{uid}
// (avatar, tutorial level, teacher edits...); the listeners keep the progress live, so attach them once.
// A logout reloads the page, so this never has to be cleared.
let progressListeningUid = null

export const useProgressStore = defineStore('progress', {
  state: () => ({
    progress: {},
    progressDownloaded: false,
  }),

  getters: {
    userId() {
      return useUserStore().uid
    },
    currentStreak: (state) => state.progress?.streak?.currentStreak || 0,
    currentLevel: (state) => state.progress?.level?.current || 1,
    nextLevelAt: (state) => state.progress?.level?.nextAt || 20,
    lastLevelAt: (state) => state.progress?.level?.lastAt || 0,
    userXp: (state) => state.progress?.total?.xp || 0,
    userInventory: (state) => state.progress?.inventory,
    userCoins: (state) => state.progress?.total?.coins || 0,
    userHealth: (state) => state.progress?.health || 70,
    userPills: (state) => state.progress?.inventory?.pill,
    userVouchers: (state) => state.progress?.inventory?.vouchers,
    userGrammarPoints: (state) => state.progress?.inventory?.grammar,
    getProgressInFloat: (state) => {
      const xp = state.progress?.total?.xp || 0
      const last = state.progress?.level?.lastAt || 0
      const next = state.progress?.level?.nextAt || 1
      let number = ((xp - last) * 100) / (next - last)
      return number / 100
    },
    userPrefs: (state) => state.progress?.prefs,
    userPacks: (state) => state.progress?.packs || {},
    // dev app: unread marker on posts (PostItemView)
    userReadThisPost: (state) => (postId) => {
      if (!postId || !state.progressDownloaded) return false
      return !!state.progress?.inventory?.postsRead?.[postId]
    },
    /* userLessonActivities: (state) => (lessonRef) => {
      return state.progress?.lessons?.[lessonRef]?.activities;
    }, */
    userFavPackId: (state) => state.progress?.favPackId,
    userEnglishLevel: (state) => state.progress?.englishLevel || null,
    onboardingFirstPack: (state) => state.progress?.onboarding?.firstPack || false, // legacy: englishLevel now written to users/{uid} (store-user.userEnglishLevel)
    aiCredits: (state) => state.progress?.aiCredits || 0,
  },

  actions: {
    setExpPoints(value) {
      if (!this.progress.total) {
        this.progress.total = {}
      }
      this.progress.total.xp = value
      this.saveProgress()
    },

    saveProgress() {
      LocalStorage.set('progress', this.progress)
    },
    userOwnsThisPill(pillId) {
      if (!pillId || !this.progressDownloaded) return false
      // Check both collected pills and created pills
      return !!this.progress?.inventory?.pill?.[pillId] || !!this.progress?.inventory?.pillCreated?.[pillId]
    },

    fetchProgress() {
      if (progressListeningUid === this.userId) return
      progressListeningUid = this.userId
      const dbRef = fbDatabase.ref('user-progress/' + this.userId)

      // Initial load
      dbRef.once(
        'value',
        (snapshot) => {
          const data = snapshot.val() || {}
          this.progress = data
          this.progressDownloaded = true
          usePacksStore().fetchPublicPacksInfos()
        },
        (error) => {
          console.error(error.message)
        }
      )

      // Listen for child_added
      dbRef.on('child_added', (snapshot) => {
        const progress = snapshot.val()
        this.progress[snapshot.key] = progress
      })

      // Listen for child_changed
      dbRef.on('child_changed', (snapshot) => {
        const key = snapshot.key
        const oldVal = this.progress[key]
        this.progress[key] = snapshot.val()

        // Clear optimistic review reward delta when server confirms packs update
        if (key === 'packs') {
          const newPacks = snapshot.val() || {}
          Object.keys(newPacks).forEach((packId) => {
            const oldProgress = oldVal?.[packId]?.studyRewardProgress
            const newProgress = newPacks[packId]?.studyRewardProgress
            if (newProgress !== oldProgress) {
              usePacksStore().clearOptimisticReviewRewardForPack(packId)
            }
          })
        }
      })

      // Listen for child_removed
      dbRef.on('child_removed', (snapshot) => {
        delete this.progress[snapshot.key]
      })
    },

    collectPill({ pillId, packId, source }) {
      if (!pillId) return

      const ownsPill = this.progress?.inventory?.pill?.[pillId]
      if (ownsPill) {
        logIt({
          type: 'pill',
          error: 'already owns pill',
          text: `try to collect pill ${pillId} from pack "${packId}" (${source})`,
        })
        return
      }

      updateDbChild({
        path: `user-progress/${this.userId}/inventory/pill`,
        data: { [pillId]: Date.now() },
      })

      logIt({
        type: 'pill',
        text: `Pill collected: ${pillId} from pack ${packId} (${source})`,
      })
      sendAnalytics('pill_collected', {
        item_id: pillId,
        pack_id: packId,
        source,
      })

    },

    removePrefsItem({ item, listKey, uid }) {
      const data = { ...(this.progress.prefs || {}) }
      data[listKey] = removeItemFromCsvList(item, data[listKey] || '')

      this.updateProgress({ uid: uid || this.userId, child: 'prefs', data })
    },

    addPrefsItem({ item, listKey, uid }) {
      const data = { ...(this.progress.prefs || {}) }
      // Remove from any other list first
      Object.keys(data).forEach((key) => {
        if (data[key].includes(item)) {
          data[key] = removeItemFromCsvList(item, data[key])
        }
      })

      data[listKey] = addItemToCsvList(item, data[listKey] || '')
      this.updateProgress({ uid: uid || this.userId, child: 'prefs', data })
    },

    toggleGrammar({ ref, operation, uid }) {
      const dbRef = fbDatabase.ref(`user-progress/${uid || this.userId}/inventory/grammar/${ref}`)
      if (operation === 'add') {
        dbRef.set(Date.now())
      } else {
        dbRef.set(null)
      }
    },

    updateProgress({ uid, child, data }) {
      if (!uid) {
        uid = this.userId
      }
      const path = `user-progress/${uid}/${child}`
      fbDatabase.ref(path).set(data, (error) => {
        if (error) {
          logIt({ error, text: error.message, path })
        }
      })
    },

    addCoins({ amount }) {
      if (!amount || !this.userId) return
      incrementDbChild({ path: `user-progress/${this.userId}/total/coins`, count: amount })
    },

    giveCoinsByUid({ uid, coins }) {
      if (!uid) {
        uid = this.userId
      }
      const postRef = fbDatabase.ref(`user-progress/${uid}/total/coins`)
      postRef.transaction((current) => {
        return (current || 0) + parseInt(coins, 10)
      })
      Notify.create(`${coins} coins given to ${uid}`)
    },
  },
})
